import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';
import { TEXT_ALIGNMENT_OPTIONS, TextAlignmentOptions, Icon } from '#const';
import { getIconUrl } from '#utils';

const SplashHero = dynamic(() => import('./SplashHero'));

Builder.registerComponent(SplashHero, {
	name: 'SplashHero',
	inputs: [
		{
			name: 'heading',
			friendlyName: 'Heading',
			type: 'richText',
			required: true,
			defaultValue: '<h1>Welcome to Our Service</h1>',
		},
		{
			name: 'headingAlignment',
			friendlyName: 'Heading Alignment',
			type: 'string',
			enum: [...TEXT_ALIGNMENT_OPTIONS],
			defaultValue: TextAlignmentOptions.LEFT,
			helperText: 'Optional text alignment for the heading.',
		},
		{
			name: 'copy',
			friendlyName: 'Body Text',
			type: 'richText',
			required: true,
			defaultValue:
				'<p>Get started with our comprehensive solution today. Our team is ready to help you achieve your goals with two simple steps.</p>',
		},
		{
			name: 'copyAlignment',
			friendlyName: 'Text Alignment',
			type: 'string',
			enum: [...TEXT_ALIGNMENT_OPTIONS],
			defaultValue: TextAlignmentOptions.LEFT,
			helperText: 'Optional text alignment for the body text.',
		},
		{
			name: 'lowerCopy',
			friendlyName: 'Additional Text',
			type: 'richText',
			helperText: 'Optional additional text below the main copy.',
		},
		{
			name: 'backgroundImage',
			friendlyName: 'Background Image Information',
			type: 'object',
			subFields: [
				{
					name: 'src',
					friendlyName: 'Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
				},
				{
					name: 'alt',
					friendlyName: 'Alt Text',
					type: 'string',
				},
			],
		},
		{
			name: 'mobileBackgroundOption',
			friendlyName: 'Mobile Background',
			type: 'string',
			enum: ['Use Desktop Image', 'None', 'Mobile Image'],
			defaultValue: 'Use Desktop Image',
			helperText: 'Choose which background to show on mobile screens.',
		},
		{
			name: 'mobileImage',
			friendlyName: 'Mobile Background Image',
			type: 'object',
			showIf: 'options.get("mobileBackgroundOption") === "Mobile Image"',
			subFields: [
				{
					name: 'src',
					friendlyName: 'Mobile Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
				},
				{
					name: 'alt',
					friendlyName: 'Alt Text',
					type: 'string',
				},
			],
		},
		{
			name: 'mobileImageHeight',
			friendlyName: 'Mobile Image Height (px)',
			type: 'number',
			defaultValue: 150,
			helperText: 'Height of the background image on mobile screens in pixels.',
			showIf: 'options.get("mobileBackgroundOption") !== "None"',
		},
		{
			name: 'textBackgroundColor',
			friendlyName: 'Text Background Color',
			type: 'color',
			defaultValue: '#FFFFFF',
		},
		{
			name: 'firstButton',
			friendlyName: 'First Button',
			type: 'object',
			subFields: [
				{
					name: 'text',
					friendlyName: 'Button Text',
					type: 'string',
					defaultValue: 'See If You Qualify',
					required: true,
				},
				{
					name: 'url',
					friendlyName: 'Button URL',
					type: 'string',
					defaultValue: '/sq',
					required: true,
				},
				{
					name: 'color',
					friendlyName: 'Button Color',
					type: 'color',
					defaultValue: '#184264',
				},
				{
					name: 'outline',
					friendlyName: 'Outline Style',
					type: 'boolean',
					defaultValue: false,
					helperText: 'Show as outlined button instead of filled.',
				},
			],
			defaultValue: {
				text: 'See If You Qualify',
				url: '/sq',
				color: '#184264',
				outline: false,
			},
		},
		{
			name: 'showSecondButton',
			friendlyName: 'Show Second Button',
			type: 'boolean',
			defaultValue: true,
			helperText: 'Toggle visibility of the second button.',
		},
		{
			name: 'secondButton',
			friendlyName: 'Second Button',
			type: 'object',
			showIf: 'options.get("showSecondButton")',
			subFields: [
				{
					name: 'text',
					friendlyName: 'Button Text',
					type: 'string',
					defaultValue: 'Learn More About the Case',
				},
				{
					name: 'url',
					friendlyName: 'Button URL',
					type: 'string',
					defaultValue: '/',
				},
				{
					name: 'color',
					friendlyName: 'Button Color',
					type: 'color',
					defaultValue: '#184264',
				},
				{
					name: 'outline',
					friendlyName: 'Outline Style',
					type: 'boolean',
					defaultValue: true,
					helperText: 'Show as outlined button instead of filled.',
				},
			],
			defaultValue: {
				text: 'Learn More About the Case',
				url: '/',
				color: '#184264',
				outline: true,
			},
		},
	],
	canHaveChildren: false,
	image: getIconUrl(Icon.HERO),
});
